import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { ClipboardCheck } from 'lucide-react';
import { useState } from 'react';
import { ComplianceAuditMonitoringFilters } from './ComplianceAuditMonitoringFilters';
import { ComplianceAuditSummaryCards } from './ComplianceAuditSummaryCards';
import { type AdminComplianceAuditRow, useAdminComplianceAuditColumns } from './columns';

export const Route = createFileRoute('/admin-console/monitoring/compliance-audit')({
    staticData: {
        permissions: ['admin_orgs:read', 'admin_users:read', 'admin_system:read', 'admin_ai:read'],
        breadcrumb: {
            title: 'Compliance audit',
        },
        ui: {
            icon: ClipboardCheck,
            title: 'Compliance audit',
            subtitle: 'Operational metrics for compliance audits across the platform',
            widthVariant: 'fullWidth',
            menuDescription: 'Monitor compliance audit cost, AI usage and files',
        },
    },
    component: ComplianceAuditMonitoringPage,
});

type ListInput = RouterInputs['admin']['complianceAudit']['list'];
type ComplianceAuditWhere = NonNullable<ListInput['where']>;
type TableParams = Pick<ListInput, 'limit' | 'offset' | 'orderBy'>;

function ComplianceAuditMonitoringPage() {
    const { trpc } = useTrpc();
    const navigate = useNavigate();

    const [filterWhere, setFilterWhere] = useState<ComplianceAuditWhere | undefined>(undefined);
    const [queryVariables, setQueryVariables] = usePathState<TableParams>('queryVariables', {
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
        orderBy: { createdAt: 'desc' },
    });

    // Reset to the first page whenever the filter set changes, so a narrowed
    // result set never strands the user on an out-of-range page.
    const handleFilterChange = (where: ComplianceAuditWhere) => {
        setFilterWhere(where);
        setQueryVariables((prev) => ({ ...prev, offset: 0 }));
    };

    const summaryQuery = trpc.admin.complianceAudit.getSummary.useQuery({ where: filterWhere });
    const { data, refetch, isFetching, error } = trpc.admin.complianceAudit.list.useQuery({
        ...queryVariables,
        where: filterWhere,
    });

    const openStats = (row: AdminComplianceAuditRow) => {
        navigate({
            to: '/admin-console/monitoring/compliance-audit/$orgId/$complianceAuditId/statistics',
            params: { orgId: row.orgId, complianceAuditId: row.id },
        });
    };

    const columns = useAdminComplianceAuditColumns(openStats);

    const tableProps = usePrismaTable({
        rowKey: 'id',
        data: data?.rows,
        total: data?.total,
        loading: isFetching,
        columns,
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: { position: 'both', showSizeChanger: true },
        columnVisibility: {
            enabled: true,
            persistKey: 'admin-compliance-audit-table-columns',
            enableReordering: true,
        },
        refreshable: { enabled: true, onRefresh: () => refetch() },
    });

    return (
        <PageController route={Route}>
            {/* `<Outlet />` (the statistics overlay) stays mounted so it keeps working even
                when the list query fails; the failed list surfaces inline in place of the table. */}
            <Outlet />

            <ComplianceAuditSummaryCards summary={summaryQuery.data} loading={summaryQuery.isPending} />

            <TableToolbar
                filters={<ComplianceAuditMonitoringFilters onChange={handleFilterChange} />}
                variant="enhanced"
            />

            {error ? (
                <InlineQueryError
                    error={error}
                    onRetry={() => void refetch()}
                    title="Couldn't load compliance audits"
                />
            ) : (
                <PrismaTable
                    {...tableProps}
                    onRow={(row) => ({
                        className: 'cursor-pointer',
                        onClick(e) {
                            openStats(row);
                            e?.preventDefault();
                        },
                    })}
                />
            )}
        </PageController>
    );
}
